/* RESPONSIBLE TEAM: team-rd-services */

import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { request } from 'embercom/lib/inbox/requests';
import type Session from 'embercom/services/session';

interface EvaluatorWireFormat {
  id: string;
  descriptor_id: string;
  name: string;
  description: string;
  evaluation_type: string;
  include_in_score: boolean;
  is_critical: boolean;
  list_options: { id: string; label: string }[];
  position: number;
  weight: number | null;
}

interface ScorecardWireFormat {
  score_descriptor_id?: string;
  evaluators: EvaluatorWireFormat[];
}

interface MonitorWireFormat {
  id: string;
  name: string;
  flag_criteria: string | null;
  status: 'live' | 'disabled';
  archived: boolean;
  created_at: string;
  updated_at: string;
  app_id: string;
  segment_ids?: number[] | null;
  backfill_status?: 'enqueueing' | 'in_progress' | 'completed' | 'failed' | null;
  default_assignee_id?: string | null;
  scorecard?: ScorecardWireFormat;
}

export interface Evaluator {
  id: string;
  descriptorId: string;
  name: string;
  description: string;
  evaluationType: string;
  includeInScore: boolean;
  isCritical: boolean;
  listOptions: { id: string; label: string }[];
  position: number;
  weight: number | null;
}

export interface Scorecard {
  scoreDescriptorId?: string;
  evaluators: Evaluator[];
}

export interface Monitor {
  id: string;
  name: string;
  flagCriteria: string | null;
  status: 'live' | 'disabled';
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  appId: string;
  segmentIds?: number[] | null;
  backfillStatus?: 'enqueueing' | 'in_progress' | 'completed' | 'failed' | null;
  defaultAssigneeId?: string | null;
  scorecard?: Scorecard;
}

function deserializeEvaluator(wire: EvaluatorWireFormat): Evaluator {
  return {
    id: wire.id,
    descriptorId: wire.descriptor_id,
    name: wire.name,
    description: wire.description,
    evaluationType: wire.evaluation_type,
    includeInScore: wire.include_in_score,
    isCritical: wire.is_critical,
    listOptions: wire.list_options,
    position: wire.position,
    weight: wire.weight,
  };
}

function deserializeScorecard(wire: ScorecardWireFormat): Scorecard {
  return {
    scoreDescriptorId: wire.score_descriptor_id,
    evaluators: wire.evaluators.map(deserializeEvaluator),
  };
}

function deserializeMonitor(wire: MonitorWireFormat): Monitor {
  return {
    id: wire.id,
    name: wire.name,
    flagCriteria: wire.flag_criteria,
    status: wire.status,
    archived: wire.archived,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
    appId: wire.app_id,
    segmentIds: wire.segment_ids,
    backfillStatus: wire.backfill_status,
    defaultAssigneeId: wire.default_assignee_id,
    scorecard: wire.scorecard ? deserializeScorecard(wire.scorecard) : undefined,
  };
}

export default class MonitorsApi extends Service {
  @service declare session: Session;

  @tracked monitors: Monitor[] = [];
  @tracked isLoading = false;

  async fetchMonitors(): Promise<Monitor[]> {
    this.isLoading = true;

    try {
      let response = await request(`/teammate_app/qa/monitors?app_id=${this.session.workspace.id}`);
      let json = await response.json();
      this.monitors = (json.monitors ?? []).map(deserializeMonitor);
      return this.monitors;
    } catch (error: unknown) {
      console.error('Failed to fetch monitors:', error);
      return [];
    } finally {
      this.isLoading = false;
    }
  }

  async getMonitorById(id: string): Promise<Monitor | undefined> {
    if (this.monitors.length === 0) {
      await this.fetchMonitors();
    }
    return this.monitors.find((m) => m.id === id);
  }
}

declare module '@ember/service' {
  interface Registry {
    'monitors-api': MonitorsApi;
  }
}
